/**
 * WelcomeVideoPopup — brand "bootup screen" that plays on entry.
 *
 * Video URL now comes from the admin-configurable /api/settings/welcome-video
 * setting (falls back to /welcome.mp4 when nothing has been overridden).
 * Admins swap the video from /admin/ad-slots without touching code.
 */
import React, { useEffect, useRef, useState, useContext } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';
import { AuthContext } from '../App';

const todayKey = () => new Date().toISOString().slice(0, 10);

const WelcomeVideoPopup = ({
  storageKey = 'fda_welcome_video_last_shown',
  src,                    // if omitted, we fetch the admin setting
  durationMs,             // if omitted, use admin-configured value
  fadeOutMs = 700,
  alwaysShow = false,
  onceEver = false,
}) => {
  const { API } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [remoteSrc, setRemoteSrc] = useState(null);
  const [remoteDuration, setRemoteDuration] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const videoRef = useRef(null);
  const timerRef = useRef(null);
  const fadeTimerRef = useRef(null);

  // Fetch admin setting once. Falls back to /welcome.mp4 on any error.
  // Also filters out STALE PREVIEW URLs — earlier forks stored asset
  // URLs pointing at *.preview.emergentagent.com pods that no longer
  // exist, and a 404 there renders as an opaque black rectangle
  // covering the landing hero. Better to swap to the bundled fallback.
  useEffect(() => {
    if (src) return; // caller override wins
    let cancelled = false;
    const isStale = (u) => typeof u === 'string' && /preview\.emergentagent\.com/i.test(u);
    axios.get(`${API}/settings/welcome-video`)
      .then((r) => {
        if (cancelled) return;
        const raw = r.data?.video_url;
        setRemoteSrc((raw && !isStale(raw)) ? raw : '/welcome.mp4');
        setRemoteDuration(r.data?.duration_ms || 7000);
        setEnabled(r.data?.enabled ?? true);
      })
      .catch(() => {
        if (cancelled) return;
        setRemoteSrc('/welcome.mp4');
        setRemoteDuration(7000);
      });
    return () => { cancelled = true; };
  }, [API, src]);

  const effectiveSrc = src || remoteSrc;
  const effectiveDuration = durationMs || remoteDuration || 7000;
  const effectiveEnabled = src ? true : enabled;

  // Decide whether to show on mount.
  useEffect(() => {
    if (!effectiveSrc || !effectiveEnabled) return;
    if (alwaysShow) {
      const t = setTimeout(() => setOpen(true), 400);
      return () => clearTimeout(t);
    }
    try {
      const last = window.localStorage.getItem(storageKey);
      if (onceEver) {
        if (last) return;
      } else if (last === todayKey()) {
        return;
      }
      const t = setTimeout(() => setOpen(true), 400);
      return () => clearTimeout(t);
    } catch (_) { /* localStorage blocked */ }
  }, [storageKey, alwaysShow, onceEver, effectiveSrc, effectiveEnabled]);

  // Autoplay muted first (browsers block unmuted autoplay on most
  // desktops + all mobiles — an unmuted-only strategy renders as a
  // frozen black frame on prod). Once it starts, try to unmute so the
  // voiceover still lands when the browser permits it.
  useEffect(() => {
    if (!open || !videoRef.current) return;
    const v = videoRef.current;
    v.muted = true;
    v.volume = 1;
    const p = v.play();
    if (p && typeof p.then === 'function') {
      p.then(() => {
        // Try unmuting after a beat so the user gets sound on real
        // interactions. Silent-fail on browsers that still refuse.
        setTimeout(() => { try { v.muted = false; } catch (_) {} }, 300);
      }).catch(() => { /* even muted play failed — auto-dismiss below via onError */ });
    }
  }, [open]);

  // Safety net: if the video never emits `loadeddata` within 4s (bad
  // src, CORS, decode fail), close the popup so the landing hero
  // renders. Prevents the "black rectangle covers the whole page"
  // launch-day bug where a stale asset URL survives a DB fork.
  useEffect(() => {
    if (!open) return undefined;
    const safety = setTimeout(() => {
      const v = videoRef.current;
      if (!v || v.readyState < 2) close();
    }, 4000);
    return () => clearTimeout(safety);
  }, [open]);

  // Auto-close timer.
  useEffect(() => {
    if (!open) return undefined;
    timerRef.current = setTimeout(() => close(), effectiveDuration);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [open, effectiveDuration]);

  const close = () => {
    if (closing) return;
    setClosing(true);
    if (videoRef.current) {
      const v = videoRef.current;
      const startVol = v.volume;
      const steps = 10;
      let i = 0;
      const iv = setInterval(() => {
        i += 1;
        v.volume = Math.max(0, startVol * (1 - i / steps));
        if (i >= steps) clearInterval(iv);
      }, fadeOutMs / steps);
    }
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    fadeTimerRef.current = setTimeout(() => {
      setOpen(false);
      setClosing(false);
      if (!alwaysShow) {
        try {
          const stamp = onceEver ? 'seen' : todayKey();
          window.localStorage.setItem(storageKey, stamp);
        } catch (_) { /* ignore */ }
      }
    }, fadeOutMs);
  };

  useEffect(() => () => {
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
  }, []);

  if (!open || !effectiveSrc) return null;

  return (
    <div
      className={`fixed inset-0 z-[110] flex items-center justify-center bg-navy transition-opacity ease-out ${closing ? 'opacity-0' : 'opacity-100 animate-in fade-in duration-500'}`}
      style={{ transitionDuration: `${fadeOutMs}ms` }}
      role="dialog"
      aria-label="Welcome video"
      data-testid="welcome-video-popup"
      onClick={close}
    >
      <div
        className={`relative w-[92vw] max-w-3xl aspect-video rounded-2xl overflow-hidden shadow-2xl border border-primary/30 bg-black transition-transform ease-out ${closing ? 'scale-[0.98]' : 'scale-100'}`}
        style={{ transitionDuration: `${fadeOutMs}ms` }}
        onClick={(e) => e.stopPropagation()}
        data-testid="welcome-video-frame"
      >
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          src={effectiveSrc}
          autoPlay
          muted
          playsInline
          onError={close}
          data-testid="welcome-video-el"
        />

        <button
          type="button"
          onClick={close}
          className="absolute top-3 right-3 inline-flex items-center justify-center h-9 w-9 rounded-full bg-black/60 backdrop-blur border border-background/20 text-background hover:bg-black/80 transition-colors"
          data-testid="welcome-video-close"
          aria-label="Close welcome video"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default WelcomeVideoPopup;
